import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filterQuestionBank } from "redux/survey/question-bank";
import { getDay } from "tools/functions/Methods";
import { useFetchSampleOptions } from "global/hooks/useFetchOptions";
import { paginationType } from "global/Interfaces/Interfaces";

import {
  AiOutlineEdit,
  AiOutlineUser,
  AiOutlineCalendar,
  AiOutlineFolderOpen,
} from "react-icons/ai";
import { FiArrowLeft } from "react-icons/fi";
import DeleteOperation from "components/tableOperation/DeleteOperation";
import Breadcrumb from "components/Breadcrumb/Breadcrumb";
import QuestionBankForm from "./form/question-bank-form";
import QuestionBankSearch from "./table/Search";
import Paginations from "global/Pagination/Pagination";
import { hasPermission } from "utils/HasPermission";

interface QuestionCardProps {
  title: string;
  questionType?: string;
  questionAssessmentMethod?: string;
  selectReplyType?: string;
  answers: { title: string; priority?: number }[];
  creator?: string;
  createdDate?: string;
  isActive: boolean;
  onEdit: () => void;
  deleteComponent: React.ReactNode;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  title,
  questionType,
  questionAssessmentMethod,
  selectReplyType,
  answers,
  creator,
  createdDate,
  isActive,
  onEdit,
  deleteComponent,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all duration-200 flex flex-col justify-between min-h-[220px] relative">
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4" dir="rtl">
          {questionType && (
            <span className="px-3 py-1 text-[11px] font-bold rounded-full bg-[#fdf0f4] text-[#7a1b2b]">
              {questionType}
            </span>
          )}
          {questionAssessmentMethod && (
            <span className="px-3 py-1 text-[11px] font-medium rounded-full bg-[#eef2f6] text-gray-600">
              {questionAssessmentMethod}
            </span>
          )}
          {selectReplyType && (
            <span className="px-3 py-1 text-[11px] font-medium rounded-full bg-[#f1f5f9] text-gray-500">
              {selectReplyType}
            </span>
          )}
          {!isActive && (
            <span className="px-3 py-1 text-[11px] font-medium rounded-full bg-red-50 text-red-500">
              غیرفعال
            </span>
          )}
        </div>

        <h3
          className="text-sm md:text-base font-bold text-gray-800 leading-relaxed mb-3 text-right"
          dir="rtl"
        >
          {title}
        </h3>

        {answers && answers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4 justify-start" dir="rtl">
            {answers.map((ans, index) => (
              <span
                key={index}
                className="px-2.5 py-1 text-[11px] font-medium rounded-full bg-gray-50 border border-gray-100 text-gray-500"
              >
                {ans.title}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* بخش پایین کارت: اطلاعات نویسنده، تاریخ و عملیات */}
      <div
        className="border-t border-gray-50 pt-3 mt-auto flex items-center justify-between text-[11px] text-gray-400"
        dir="rtl"
      >
        <div className="flex items-center gap-3">
          {creator && (
            <span className="flex items-center gap-1">
              <AiOutlineUser className="text-gray-400" size={14} />
              <span className="text-gray-600">{creator}</span>
            </span>
          )}
          {createdDate && (
            <span className="flex items-center gap-1">
              <AiOutlineCalendar className="text-gray-400" size={14} />
              <span className="text-gray-500">{createdDate}</span>
            </span>
          )}
        </div>

        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {hasPermission("SURVEY_QUESTION_EDIT") && (
            <button
              type="button"
              className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition"
              onClick={onEdit}
              title="ویرایش سوال"
            >
              <AiOutlineEdit size={16} />
            </button>
          )}
          {hasPermission("SURVEY_QUESTION_DELETE") && (
            <div className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition flex items-center justify-center">
              {deleteComponent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const QuestionBankNewUi = () => {
  const dispatch = useDispatch();

  const { dataOptions: replyTypeSelect } = useFetchSampleOptions(
    "/customer-api/question/replyTypeSelect"
  );
  const { dataOptions: questionTypeSelect } = useFetchSampleOptions(
    "/customer-api/question/questionTypeSelect"
  );
  const { dataOptions: questionAssessmentMethodSelect } = useFetchSampleOptions(
    "/customer-api/question/questionAssessmentMethodSelect"
  );

  const { questionBankData, fetchpost, filter } = useSelector(
    (state: any) => state.QuestionBankSlice
  );
  const { pageNumbers } = useSelector(
    (state: paginationType) => state.paginate
  );

  const [modals, setModals] = useState({
    questionBank: { open: false, currentData: null },
  });

  const refreshTable = () => {
    dispatch(
      filterQuestionBank({
        ...filter,
        pageSize: 9,
        pageNumber: pageNumbers,
      }) as any
    );
  };

  return (
    <div
      className="w-full px-4 md:px-8 py-6 bg-[#f8f9fa] min-h-screen"
      dir="rtl"
    >
      <Breadcrumb curentPage="بانک سوالات" beforePage="" />

      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="flex items-center gap-2 px-4 py-2 text-xs md:text-sm font-semibold text-gray-600 bg-white rounded-xl border border-gray-100 hover:bg-gray-50 transition shadow-sm"
        >
          بازگشت به منو
          <FiArrowLeft size={16} />
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-6">
        <div className="bg-gradient-to-r from-[#fff5f8] via-[#fdf2f6] to-[#fff7fa] rounded-2xl p-4 md:p-6 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-pink-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fce8f0] flex items-center justify-center text-[#7a1b2b]">
              <AiOutlineFolderOpen size={22} />
            </div>
            <div>
              <h2 className="text-base md:text-lg font-extrabold text-gray-800">
                مدیریت بانک سوالات
              </h2>
              <p className="text-[11px] text-gray-400 mt-0.5">
                مشاهده، فیلتر و مدیریت تمام سوالات تعریف شده
              </p>
            </div>
          </div>
          {hasPermission("SURVEY_QUESTION_CREATE") && (
            <button
              type="button"
              onClick={() => {
                setModals((prev) => ({
                  ...prev,
                  questionBank: { open: true, currentData: null },
                }));
              }}
              className="px-6 py-2.5 rounded-full bg-[#7a1b2b] text-white text-xs md:text-sm font-bold shadow-md hover:bg-[#631221] transition-all duration-200 hover:-translate-y-0.5"
            >
              + سوال جدید
            </button>
          )}
        </div>

        <div className="mb-6 bg-gray-50/50 rounded-2xl p-4 border border-gray-100/50">
          <QuestionBankSearch
            options={{
              replyTypeSelect: replyTypeSelect?.options,
              questionTypeSelect:questionTypeSelect?.options ,
              questionAssessmentMethodSelect:questionAssessmentMethodSelect?.options ,
            }}
          />
        </div>

        {fetchpost ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 border-4 border-[#7a1b2b] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-gray-500 font-medium">
              در حال بارگذاری سوالات...
            </p>
          </div>
        ) : questionBankData?.content?.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm text-gray-400">
              هیچ سوالی در بانک سوالات یافت نشد.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {questionBankData?.content?.map((item: any) => {
                const answerList =
                  item?.questionContentList?.map((ans: any) => ({
                    title: ans.title,
                    priority: ans.priority,
                  })) || [];

                return (
                  <QuestionCard
                    key={item.id}
                    title={item.title}
                    questionType={item.questionType?.text}
                    questionAssessmentMethod={
                      item.questionAssessmentMethod?.text
                    }
                    selectReplyType={item.selectReplyType?.text}
                    answers={answerList}
                    creator={item.modifier}
                    createdDate={getDay(item.createdDate)}
                    isActive={item.isActive}
                    onEdit={() => {
                      setModals((prev) => ({
                        ...prev,
                        questionBank: { open: true, currentData: item },
                      }));
                    }}
                    deleteComponent={
                      <DeleteOperation
                        itemId={item.id}
                        title=""
                        handleDeleteActionNewData={refreshTable}
                        route="/customer-api/question/v1/s1/delete"
                      />
                    }
                  />
                );
              })}
            </div>
            <Paginations pageData={questionBankData?.totalElements} />
          </>
        )}
      </div>

      <QuestionBankForm
        options={{
          replyTypeSelect: replyTypeSelect?.options,
          questionTypeSelect:questionTypeSelect?.options ,
          questionAssessmentMethodSelect:questionAssessmentMethodSelect?.options ,
        }}
        currentData={modals.questionBank.currentData}
        open={modals.questionBank.open}
        setOpen={() =>
          setModals({
            ...modals,
            questionBank: { open: false, currentData: null },
          })
        }
        refreshTable={refreshTable}
      />
    </div>
  );
};

export default QuestionBankNewUi;
